// ThemisRuleSetList.tsx
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FormikProvider, useFormik } from "formik";
import { useHistory } from "react-router-dom";

import { useAlert } from "../../../components/Alert/Alerts";
import Button from "../../../components/Button/Button";
import { CardBody } from "../../../components/DashboardCard/DashboardCard";
import TextField from "../../../components/FormFields/TextField";
import { SelectBox } from "../Judging/JudgingConfig/EntrySetTab";

import ConfirmModal from "./ConfirmModal";

import DraggableRuleSetList from "./DraggableRuleSetList";
import {
  ThemisRuleSetListItemDto,
  createRuleSet,
  deleteRuleSet,
  getRuleSet,
  getRuleSets,
  slugifyKeyFromName,
  updateRuleSet,
} from "./manageThemisRuleSets";

const OptionsSection = styled.div`
  display: flex;
  width: 100%;
  align-self: stretch;
  flex: 1;
  gap: 2rem;
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colorBorderLight};
  min-height: 400px;
`;

const PAGE_SIZE = 200;

export default function ThemisRuleSetList() {
  const { addNewAlert } = useAlert();
  const history = useHistory();

  const [ruleSets, setRuleSets] = useState<ThemisRuleSetListItemDto[]>([]);
  const [newRuleSetName, setNewRuleSetName] = useState("");
  const [ruleSetPendingDelete, setRuleSetPendingDelete] =
    useState<ThemisRuleSetListItemDto | null>(null);

  const formik = useFormik({
    initialValues: {},
    onSubmit: () => {},
  });

  const sortedRuleSets = useMemo(() => {
    return ruleSets
      .slice()
      .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  }, [ruleSets]);

  const load = () => {
    getRuleSets({ pageNumber: 1, pageSize: PAGE_SIZE })
      .then((res) => {
        if (res.status === 200) setRuleSets(res.data ?? []);
        else setRuleSets([]);
      })
      .catch(() => {
        setRuleSets([]);
        addNewAlert({ type: "error", message: "Failed to load rule sets." });
      });
  };

  useEffect(() => {
    load();
  }, []);

  const handleAddRuleSet = () => {
    const trimmed = newRuleSetName.trim();
    if (!trimmed) return;

    if (ruleSets.some((r) => r.name.toLowerCase() === trimmed.toLowerCase())) {
      addNewAlert({ type: "error", message: "RuleSet already exists." });
      return;
    }

    const key = slugifyKeyFromName(trimmed);

    createRuleSet({
      key,
      name: trimmed,
      description: null,
      isEnabled: true,
      priority: 0,
    })
      .then((res) => {
        if (res.status === 200) {
          setNewRuleSetName("");
          addNewAlert({ type: "success", message: "RuleSet created." });
          load();
          return;
        }
        throw new Error("Create failed");
      })
      .catch(() => {
        addNewAlert({ type: "error", message: "Failed to create RuleSet." });
      });
  };

  const confirmDeleteRuleSet = (id: number) => {
    const found = ruleSets.find((r) => r.id === id);
    if (found) setRuleSetPendingDelete(found);
  };

  const handleConfirmDelete = () => {
    if (!ruleSetPendingDelete) return;
    const id = ruleSetPendingDelete.id;

    deleteRuleSet(id)
      .then(() => {
        setRuleSets((prev) => prev.filter((r) => r.id !== id));
        addNewAlert({ type: "success", message: "RuleSet deleted." });
      })
      .catch(() => {
        addNewAlert({ type: "error", message: "Failed to delete RuleSet." });
      })
      .finally(() => setRuleSetPendingDelete(null));
  };

  const handleReorder = async (orderedIds: number[]) => {
    const details = await Promise.all(
      orderedIds.map((id) => getRuleSet(id).then((r) => r.data)),
    );

    await Promise.all(
      details.map((rs, idx) =>
        updateRuleSet(rs.id, {
          name: rs.name,
          description: rs.description ?? null,
          isEnabled: rs.isEnabled,
          priority: (idx + 1) * 10,
        }),
      ),
    );

    load();
  };

  return (
    <FormikProvider value={formik}>
      <CardBody className="flex gap-[2rem]">
        <OptionsSection>
          <div className="flex-1 w-full">
            <h2 className="mb-[.25rem]">Rule Sets</h2>
            <p className="mb-[1rem] text-sm opacity-70">
              Create and reorder RuleSets. Edit to manage membership.
            </p>

            <div className="flex gap-[.5rem] mb-[1rem] w-full">
              <TextField
                name="newRuleSetName"
                className="w-full"
                placeholder="New RuleSet Name"
                value={newRuleSetName}
                onChange={(e) => setNewRuleSetName(e.target.value)}
              />
              <Button icon="plus" onClick={handleAddRuleSet}>
                Add
              </Button>
            </div>

            <div className="w-full">
              <DraggableRuleSetList
                ruleSets={sortedRuleSets}
                onEdit={(id) => history.push(`/themis/rulesets/ruleset/${id}`)}
                onDelete={(id) => confirmDeleteRuleSet(id)}
                onReorder={handleReorder}
              />
            </div>

            {sortedRuleSets.length === 0 ? (
              <SelectBox>No rule sets</SelectBox>
            ) : null}
          </div>
        </OptionsSection>

        <ConfirmModal
          isOpen={!!ruleSetPendingDelete}
          title="Delete RuleSet?"
          message={
            ruleSetPendingDelete
              ? `Are you sure you want to delete "${ruleSetPendingDelete.name}"? This cannot be undone.`
              : ""
          }
          confirmText="Delete"
          cancelText="Cancel"
          onCancel={() => setRuleSetPendingDelete(null)}
          onConfirm={handleConfirmDelete}
        />
      </CardBody>
    </FormikProvider>
  );
}
